import { invoke } from "@tauri-apps/api/core";
import { translate } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { readNativeDroppedFile } from "@/lib/native-clipboard-files";
import { useNotificationStore } from "@/stores/notification-store";

export type NativeFileFilter = {
  name: string;
  extensions: readonly string[];
};

/** `pick_files`: the picks this window may use, and how many it may not. */
type PickedFiles = { paths: string[]; rejected: number };

function report(id: string, title: string, description?: string) {
  useNotificationStore.getState().addSystemToast({
    id: `native-file-pick-${id}`,
    title,
    level: "error",
    description,
  });
}

type PickOptions = { filter?: NativeFileFilter; multiple: boolean };

/**
 * The desktop app's file picker. WebKit's `<input type="file">` panel opens
 * behind the Workspace overlay; this one is owned by the calling window, so
 * it always shows above it. Null when it could not open (reported).
 */
async function pickGranted({
  filter,
  multiple,
}: PickOptions): Promise<PickedFiles | null> {
  try {
    return await invoke<PickedFiles>("pick_files", {
      filter: filter ?? null,
      multiple,
    });
  } catch {
    report("dialog", translate(commonMessages, "somethingWentWrongRetry"));
    return null;
  }
}

/**
 * The picked files, read (empty on cancel); files that cannot be opened are
 * left out and reported together.
 */
export async function pickNativeFiles(options: PickOptions): Promise<File[]> {
  const picked = await pickGranted(options);
  if (!picked) return [];
  const { files, unread } = await readGrantedFiles(picked.paths);
  reportUnopenedFiles(picked.rejected + unread.length);
  return files;
}

/**
 * The picked paths this window was granted, to read or link (empty on
 * cancel); picks that cannot be used are reported together.
 */
export async function pickNativePaths(options: PickOptions): Promise<string[]> {
  const picked = await pickGranted(options);
  if (!picked) return [];
  reportUnopenedFiles(picked.rejected);
  return picked.paths;
}

/** Why a granted file could not be read. */
export type UnreadReason = "too-large" | "folder" | "unreadable";

export interface UnreadFile {
  name: string;
  reason: UnreadReason;
}

/** The read errors `dropped_files.rs` gives for a reason the page names. */
const READ_ERRORS: Readonly<Record<string, UnreadReason>> = {
  "Files must be 20 MB or smaller": "too-large",
  "Folders cannot be added": "folder",
};
/** Files read at once: each arrives as base64 of up to 20 MB. */
const READS_AT_ONCE = 3;

/** A path's last part, its file name. */
export function baseName(path: string): string {
  return path.split(/[\\/]/).pop() || path;
}

/** Why a grant could not be used, from the error `dropped_files.rs` gave. */
export function grantErrorReason(error: unknown): UnreadReason {
  return READ_ERRORS[String(error)] ?? "unreadable";
}

/**
 * The files this window was granted (a native pick or an OS drop), read
 * once each, {@link READS_AT_ONCE} at a time and in order, well inside the
 * grant's two minutes. Those that cannot be read come back as `unread`,
 * with why.
 */
export async function readGrantedFiles(
  paths: readonly string[],
): Promise<{ files: File[]; unread: UnreadFile[] }> {
  const results: (File | UnreadFile)[] = new Array(paths.length);
  let next = 0;
  const work = async () => {
    for (let index = next++; index < paths.length; index = next++) {
      const path = paths[index]!;
      try {
        results[index] = await readNativeDroppedFile(path);
      } catch (error) {
        results[index] = {
          name: baseName(path),
          reason: grantErrorReason(error),
        };
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(READS_AT_ONCE, paths.length) }, work),
  );
  return {
    files: results.filter((result): result is File => result instanceof File),
    unread: results.filter(
      (result): result is UnreadFile => !(result instanceof File),
    ),
  };
}

/** One toast for `count` files that could not be opened. */
export function reportUnopenedFiles(count: number): void {
  if (count <= 0) return;
  report(
    "files",
    translate(commonMessages, "filesNotOpened", { count }),
    translate(commonMessages, "filesNotOpenedHint"),
  );
}
